import { cast } from 'mobx-state-tree';
import moment from 'moment';
import {
  IDeptWorkerModel,
  IRequestsReportCategoryModel,
} from '../store/models/reports-models/requestsReportStore';
import { IRequestStatus } from '../types';

/** Ключ и название группы заявок без исполнителя (бэк отдаёт для неё null) */
export const NO_EXECUTOR_DEPARTMENT_ID = 'no-executor';
export const NO_EXECUTOR_LABEL = 'Без исполнителя';

/** Компаратор id отделов: «Без исполнителя» всегда последним */
export const compareNoExecutorLast = (a: string, b: string): number =>
  Number(a === NO_EXECUTOR_DEPARTMENT_ID) -
  Number(b === NO_EXECUTOR_DEPARTMENT_ID);

interface IDispatcherEmployeeData {
  employee_id: string | null;
  employee_fullname: string | null;
  employee_position_name: string | null;
  total_requests: number;
  avg_rate: number | null;
  request_statuses: { status: IRequestStatus; count: number }[];
}

interface IDispatcherDepartmentData {
  department_id: string | null;
  department_name: string | null;
  department_total_requests: number;
  department_request_statuses: { status: IRequestStatus; count: number }[];
  employees: IDispatcherEmployeeData[];
}

interface ICategoriesData {
  created_at: string;
  categories: ICategory[];
}

interface ICategory {
  name: string;
  count: number;
  subcategories?: ICategory[];
}

export const transformWorkers = (
  data: IDispatcherDepartmentData[],
  executors: string[]
): { [key: string]: IDeptWorkerModel[] } => {
  const result: { [key: string]: IDeptWorkerModel[] } = {};

  data.forEach((dept) => {
    let workers: IDeptWorkerModel[] = dept.employees.map((emp) => {
      const statuses: Record<string, number> = {};
      emp.request_statuses.forEach(({ status, count }) => {
        statuses[status] = count;
      });

      return {
        id: emp.employee_id,
        name: emp.employee_fullname,
        department_name:
          dept.department_id === null
            ? NO_EXECUTOR_LABEL
            : dept.department_name,
        position_name: emp.employee_position_name,
        statuses: cast(statuses),
        total: emp.total_requests,
        avg_rate: emp.avg_rate !== null ? String(emp.avg_rate) : null,
      };
    });

    if (executors.length > 0) {
      workers = workers.filter((w) => !!w.id && executors.includes(w.id));
    }

    if (workers.length > 0) {
      result[dept.department_id ?? NO_EXECUTOR_DEPARTMENT_ID] = workers;
    }
  });

  return result;
};

function processCategories(
  categories: ICategory[],
  createdAt: string,
  result: IRequestsReportCategoryModel[]
) {
  categories.forEach((category: ICategory) => {
    let categoryEntry = result.find(
      (c: IRequestsReportCategoryModel) => c.name === category.name
    );

    if (!categoryEntry) {
      categoryEntry = {
        name: category.name,
        dates: [],
        subcategories: [],
        count: 0,
      };
      result.push(categoryEntry);
    }

    categoryEntry.dates.push({ date: createdAt, count: category.count });
    categoryEntry.count += category.count;

    category.subcategories?.forEach((subcategory: ICategory) => {
      if (!subcategory.name) {
        return;
      }

      let subcategoryEntry = categoryEntry?.subcategories?.find(
        (sc: any) => sc.name === subcategory.name
      );

      if (!subcategoryEntry) {
        subcategoryEntry = {
          name: subcategory.name,
          dates: [],
          count: 0,
        };
        categoryEntry?.subcategories?.push(subcategoryEntry);
      }

      subcategoryEntry.dates.push({
        date: createdAt,
        count: subcategory.count,
      });
      subcategoryEntry.count += subcategory.count;
    });
  });
}

export const transformCategories = (
  jsonData: ICategoriesData[],
  month: string
): IRequestsReportCategoryModel[] => {
  const processedData: IRequestsReportCategoryModel[] = [];

  jsonData.forEach((item: ICategoriesData) => {
    const createdAt = item.created_at;
    const categories = item.categories;

    if (month === moment(createdAt).format('MM.yyyy')) {
      processCategories(categories, createdAt, processedData);
    }
  });

  processedData.forEach((el: IRequestsReportCategoryModel) => {
    el.subcategories?.sort(
      (a: IRequestsReportCategoryModel, b: IRequestsReportCategoryModel) =>
        b.count - a.count
    );
  });
  processedData.sort((a, b) => b.count - a.count);

  return processedData;
};
